import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Paper,
} from '@mui/material';
import React from 'react';
import type { FundSetup } from '../../data/types';
import { GROUP_LABELS, type SectionGroup } from '../../domain/setup/fieldTypes';
import { sectionValid } from '../../domain/setup/setupRules';
import { SETUP_SECTIONS } from '../../domain/setup/sections';

const GROUPS: SectionGroup[] = ['FUND', 'CLASS', 'ACTIVATE', 'OFFER'];

type NavSetup = Pick<FundSetup, 'status' | 'completed' | 'values'>;

export function isSectionLocked(setup: Pick<FundSetup, 'status'>, group: SectionGroup) {
  return group === 'OFFER' && setup.status === 'DRAFT';
}

function stepIcon(locked: boolean, done: boolean, needsWork: boolean) {
  if (locked) return <LockOutlinedIcon fontSize="small" color="disabled" titleAccess="Locked" />;
  if (needsWork) {
    return <ErrorOutlineIcon fontSize="small" color="warning" titleAccess="Needs attention" />;
  }
  if (done) return <CheckCircleIcon fontSize="small" color="success" titleAccess="Done" />;
  return <RadioButtonUncheckedIcon fontSize="small" color="disabled" titleAccess="Not started" />;
}

export default function SetupSideNav({
  setup,
  current,
  onSelect,
  isValid = (sectionId) => sectionValid(sectionId, setup.values),
  flagged = [],
}: {
  setup: NavSetup;
  current: string;
  onSelect: (sectionId: string) => void;
  isValid?: (sectionId: string) => boolean;
  flagged?: string[];
}) {
  return (
    <Paper variant="outlined" sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
      <List dense disablePadding aria-label="Setup steps">
        {GROUPS.map((group) => (
          <React.Fragment key={group}>
            <ListSubheader sx={{ lineHeight: '32px', bgcolor: 'transparent', fontWeight: 700 }}>
              {GROUP_LABELS[group]}
            </ListSubheader>
            {SETUP_SECTIONS.filter((section) => section.group === group).map((section) => {
              const locked = isSectionLocked(setup, group);
              const registerActive = group !== 'OFFER' && setup.status !== 'DRAFT';
              const valid = section.blocks.length === 0 || isValid(section.id);
              const checked = setup.completed.includes(section.id) || flagged.includes(section.id);
              const done = registerActive || (checked && valid);
              const needsWork = !registerActive && checked && !valid;
              return (
                <ListItemButton
                  key={section.id}
                  selected={section.id === current}
                  disabled={locked}
                  onClick={() => onSelect(section.id)}
                  sx={{ py: 0.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {stepIcon(locked, done, needsWork)}
                  </ListItemIcon>
                  <ListItemText primary={section.title} />
                </ListItemButton>
              );
            })}
          </React.Fragment>
        ))}
      </List>
    </Paper>
  );
}
